import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Box, Typography } from '@mui/material';
import { useChartTokens, axisProps, gridProps, tooltipStyle, categoryAccent } from './chartTokens';
import { formatINR, formatNumber } from './primitives';

export interface TrendPoint {
  label: string;
  /** null means "no snapshot that day" — rendered as a gap, never interpolated. */
  value: number | null;
}

interface TrendAreaProps {
  points: TrendPoint[];
  category?: string;
  /** Format the y axis and tooltip as currency. */
  currency?: boolean;
  height?: number | string;
  /** Show the y axis. Off for small sparkline-style tiles. */
  showAxis?: boolean;
}

/**
 * Single-series gradient area chart.
 *
 * One series, so there is deliberately no legend — the widget title names it.
 * `connectNulls` is off: a missing day must read as a gap rather than a
 * straight line drawn through data that was never recorded.
 */
const TrendArea: React.FC<TrendAreaProps> = ({
  points,
  category,
  currency = false,
  height = '100%',
  showAxis = true,
}) => {
  const t = useChartTokens();
  const accent = categoryAccent(category);
  const gradientId = React.useId().replace(/:/g, '');

  const withData = points.filter((p) => p.value !== null);
  if (withData.length === 0) {
    return (
      <Box sx={{ height, display: 'grid', placeItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          No history recorded yet
        </Typography>
      </Box>
    );
  }

  const fmt = (v: number) => (currency ? formatINR(v, true) : formatNumber(v));

  return (
    <Box sx={{ height, width: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 8, right: 8, left: showAxis ? -12 : 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity={0.28} />
              <stop offset="100%" stopColor={accent} stopOpacity={0.02} />
            </linearGradient>
          </defs>

          <CartesianGrid {...gridProps(t)} />
          <XAxis
            dataKey="label"
            {...axisProps(t)}
            interval="preserveStartEnd"
            minTickGap={28}
            hide={!showAxis}
          />
          {showAxis && <YAxis {...axisProps(t)} width={54} tickFormatter={fmt} />}

          {/* Interactive by default — an HTML chart with no hover layer is a
              picture of data rather than a view of it. */}
          <Tooltip
            {...tooltipStyle(t)}
            formatter={(v: any) => [fmt(Number(v)), '']}
            labelFormatter={(l: any) => String(l)}
          />

          <Area
            type="monotone"
            dataKey="value"
            stroke={accent}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            connectNulls={false}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: t.surface }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default TrendArea;
